import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useSearch } from '../context/SearchContext';
import debounce from 'lodash/debounce';
import { GetCommonSettings } from '@themes/common/data';
import clsx from 'clsx';
export const SearchInput = ({ searchTypes, maxResults = 10, onResultSelect }) => {
    const { translations } = GetCommonSettings();
    const debouncedSearch = useRef(debounce(() => { }, 300));
    const inputRef = useRef(null);
    const resultsRef = useRef(null);
    const [searchStatus, setSearchStatus] = useState('');
    const { state, setQuery, setOpen, setSelectedIndex, selectItem, searchItems } = useSearch();
    const { query, selectedIndex, isOpen, searchItems: items } = state;
    const hasQuery = query.trim() && query.length > 0;
    // Create a unique ID for the combobox
    const searchInputId = 'site-search-input';
    const searchResultsId = 'search-results-list';
    // Handle input change without debounce
    const handleChange = useCallback((value) => {
        setQuery(value);
        setOpen(true);
        // Update screen reader status to indicate searching
        setSearchStatus('Searching...');
        debouncedSearch.current.cancel();
        debouncedSearch.current = debounce(() => {
            searchItems(value, searchTypes, maxResults);
            // Update status for screen readers after search completes
            setTimeout(() => {
                if (items.length > 0) {
                    setSearchStatus(`${items.length} results found. Use up and down arrow keys to navigate results.`);
                }
                else {
                    setSearchStatus('No results found.');
                }
            }, 350); // Wait a bit longer than debounce to ensure results are ready
        }, 300);
        debouncedSearch.current();
    }, [setQuery, searchItems, searchTypes, maxResults, setOpen, items.length]);
    // Handle clearing the input
    const handleClear = () => {
        var _a;
        setQuery('');
        setOpen(false);
        setSearchStatus('Search cleared');
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    // Handle key navigation
    const handleKeyDown = (e) => {
        var _a, _b, _c;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (selectedIndex < items.length - 1) {
                    setSelectedIndex(selectedIndex + 1);
                    setSearchStatus(`${((_a = items[selectedIndex + 1]) === null || _a === void 0 ? void 0 : _a.item.title) || ''} selected`);
                }
                else {
                    setSelectedIndex(-1);
                    setSearchStatus('Search input selected');
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (selectedIndex > 0) {
                    setSelectedIndex(selectedIndex - 1);
                    setSearchStatus(`${((_b = items[selectedIndex - 1]) === null || _b === void 0 ? void 0 : _b.item.title) || ''} selected`);
                }
                else if (selectedIndex === 0) {
                    setSelectedIndex(-1);
                    setSearchStatus('Search input selected');
                }
                else {
                    setSelectedIndex(items.length - 1);
                    setSearchStatus(`${((_c = items[items.length - 1]) === null || _c === void 0 ? void 0 : _c.item.title) || ''} selected`);
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && items[selectedIndex]) {
                    const searchItem = items[selectedIndex];
                    if (onResultSelect) {
                        onResultSelect(searchItem);
                    }
                    else {
                        selectItem(searchItem);
                    }
                    setSearchStatus(`Navigating to ${searchItem.item.title}`);
                }
                else {
                    handleSubmit(e);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setOpen(false);
                setSearchStatus('Search results closed');
                break;
            case 'Tab':
                // Don't prevent default tab behavior
                setOpen(false);
                break;
        }
    };
    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        if (query.trim()) {
            setSearchStatus(`Searching for ${query}`);
            window.location.href = `/search?q=${encodeURIComponent(query.trim())}`;
        }
    };
    // Announce selected result to screen readers
    useEffect(() => {
        if (selectedIndex >= 0 && items[selectedIndex]) {
            const selectedItem = items[selectedIndex];
            setSearchStatus(`${selectedItem.item.title} selected`);
        }
    }, [selectedIndex, items]);
    return (_jsxs("div", Object.assign({ className: 'search-container' }, { children: [_jsxs("form", Object.assign({ role: 'search', "aria-labelledby": 'search-form-label', onSubmit: handleSubmit, className: 'relative w-full rounded-md overflow-hidden h-11 border-solid border-gray-300 border focus-within:ring-2 focus-within:ring-search-button transition-all focus:border-search-button focus-within:border-search-button hover:border-search-button' }, { children: [_jsx("label", Object.assign({ id: 'search-form-label', htmlFor: searchInputId, className: 'sr-only' }, { children: translations.search.search_for_products })), _jsx("div", Object.assign({ className: 'absolute inset-y-0 left-0 flex items-center justify-center pointer-events-none bg-search-button w-11 h-11' }, { children: _jsx("svg", Object.assign({ className: 'w-5 h-5 text-search-button-icon', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg', "aria-hidden": 'true' }, { children: _jsx("path", { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2', d: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' }) })) })), _jsx("input", { ref: inputRef, id: searchInputId, type: 'search', className: clsx('w-full p-2 px-14 text-base! border-none focus:outline-hidden', {
                            'pr-2': !hasQuery,
                        }), placeholder: translations.search.search_for_products, value: query, onChange: (e) => handleChange(e.target.value), onKeyDown: handleKeyDown, onFocus: (e) => handleChange(e.target.value), 
                        // Add ARIA attributes for accessibility
                        role: 'combobox', "aria-controls": searchResultsId, "aria-expanded": isOpen && !!hasQuery, "aria-autocomplete": 'list', "aria-activedescendant": selectedIndex >= 0 ? `search-result-${selectedIndex}` : undefined }), hasQuery && (_jsx("button", Object.assign({ type: 'button', onClick: handleClear, "aria-label": 'Clear search', className: 'absolute inset-y-0 right-0 flex items-center justify-center w-11 h-11 text-gray-400 hover:text-gray-600 transition-colors bg-transparent cursor-pointer border-none focus:outline-hidden focus:ring-2 focus:ring-search-button focus:ring-offset-1' }, { children: _jsx("svg", Object.assign({ className: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg', "aria-hidden": 'true' }, { children: _jsx("path", { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2', d: 'M6 18L18 6M6 6l12 12' }) })) })))] })), _jsx("div", Object.assign({ "aria-live": 'polite', className: 'sr-only', role: 'status' }, { children: searchStatus })), _jsx("div", { ref: resultsRef, id: searchResultsId })] })));
};
